import { AtomStateManagerProvider } from '@common/AtomStateManagerProvider';
import { useAllConnectionSpec } from '@common/Fabric/Connections/hooks';
import { OrchestrationConnectionType } from '@common/Fabric/Connections/types';
import { useOrchestrationFabricConnections } from '@common/Fabric/Connections/useOrchestrationFabricConnections';
import { FabricContextProvider } from '@common/Fabric/OrchestrationSecrets/FarbricIdProvider';
import { OrchestrationDbxAuthType } from '@common/Fabric/types/orchestration';
import { additionalComponentsForOrchConnection } from '@common/sqlIde/fabric/connections/CreateConnectionDialog';
import { COMMON_ACTION_TYPES, CommonGraph, CommonState, EditMode } from '@common/types';
import { VAppFabricProviderTypeWithoutProphecyManaged } from '@common/VerticalApps/types';
import { useProviderForm } from '@pages/Fabric/common/ProviderFormContext';
import { DatabricksAuthType, UnifiedDbxFormData } from '@pages/Fabric/types';
import { extractKGCard } from '@pages/Fabric/utils/extractKGCard';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { Parser } from 'frontend/core/src/Parser';
import { useMemo, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Store } from 'redux';

import { getKgScheduleDisplay, getTimezoneLabel } from '../kgScheduleDisplay';
import { ReadOnlySectionCard } from '../ReadOnlyFabricView';

export const KnowledgeGraphIndexerForm = ({
  provider,
  isReadonly
}: {
  provider: VAppFabricProviderTypeWithoutProphecyManaged;
  isReadonly: boolean;
}) => {
  const { formContext, readonly, readonlyMessage, fabricId } = useProviderForm();
  const dbxFormContext = formContext as unknown as UseFormReturn<UnifiedDbxFormData>;
  const { watch, setValue } = dbxFormContext;

  // provider's runtime value always matches an OrchestrationConnectionType member for the
  // providers this form is registered for (Databricks today) — the cast is for the enum's
  // nominal typing only, connectionSpecs.kind really is keyed by these same string values.
  const connectionKind = provider as unknown as OrchestrationConnectionType;

  const connectionSpecs = useAllConnectionSpec();
  const kgSpec = useMemo(() => {
    const spec = connectionSpecs.find((s) => s.kind === connectionKind);
    return spec?.uiSpec ? extractKGCard(spec.uiSpec) : null;
  }, [connectionSpecs, connectionKind]);

  // Resolve the real connection so the KG banner crawls with its id, not a placeholder.
  const { connections } = useOrchestrationFabricConnections();
  const connection = connections.find((c) => c.kind === connectionKind);

  const authType = watch('unifiedFabricData.authType');
  const oAuthType = watch('unifiedFabricData.oAuthDetails.type');
  const kgConfig = watch('unifiedFabricData.kgConfig');

  const storeRef = useRef<Store<CommonState, COMMON_ACTION_TYPES<CommonGraph>, {}>>();

  // Seeds the KG card's own conditionals (authType/oAuthType), which mirror the connection's auth
  // and aren't editable from within the card itself. Re-seeded (resetting kgConfig edits) only when
  // those change — kgConfig itself is intentionally excluded so live edits aren't clobbered on every render.
  const stateProps = useMemo(
    () => ({
      rootPath: '$.connections',
      initialProcess: {
        id: connection?.id ?? '',
        component: connectionKind,
        name: connection?.name ?? '',
        kind: connectionKind,
        properties: {
          authType:
            authType === DatabricksAuthType.token ? OrchestrationDbxAuthType.PAT : OrchestrationDbxAuthType.OAUTH,
          oAuthType,
          kgConfig
        }
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above; kgConfig excluded on purpose
    [authType, oAuthType, connectionKind, connection?.id, connection?.name]
  );

  const onPropertiesDidChange = usePersistentCallback((state: CommonState) => {
    const updatedProperties = state.currentGraph?.processes?.[state.currentComponentId ?? '']?.properties as
      | { kgConfig?: UnifiedDbxFormData['unifiedFabricData']['kgConfig'] }
      | undefined;

    if (updatedProperties?.kgConfig) {
      setValue('unifiedFabricData.kgConfig', updatedProperties.kgConfig, { shouldDirty: true });
    }
  });

  if (isReadonly) {
    if (!kgConfig) {
      return null;
    }

    const kgEnabled = Boolean(kgConfig.enabled);
    const schedule = getKgScheduleDisplay(kgConfig.kgSchedule?.cron);
    const timezoneLabel = getTimezoneLabel(kgConfig.kgSchedule?.timeZone);

    return (
      <ReadOnlySectionCard
        section={{
          title: 'Knowledge Graph Indexer',
          badge: kgEnabled ? { label: 'Configured', variant: 'success' } : { label: 'Unconfigured', variant: 'error' },
          rows: [
            { label: 'Schedule', value: kgEnabled ? schedule : undefined },
            { label: 'Timezone', value: kgEnabled ? timezoneLabel : undefined }
          ]
        }}
      />
    );
  }

  if (!kgSpec) {
    return null;
  }

  return (
    <FabricContextProvider fabricId={fabricId || ''}>
      <AtomStateManagerProvider
        stateProps={stateProps}
        storeRef={storeRef}
        ideMode={readonly ? EditMode.disabled : EditMode.enabled}
        disabledMessage={readonlyMessage}
        additionalComponents={additionalComponentsForOrchConnection}
        onPropertiesDidChange={onPropertiesDidChange}>
        <Parser specs={kgSpec} />
      </AtomStateManagerProvider>
    </FabricContextProvider>
  );
};
